// ============================================================================
// TimeActionCard — record a shift a worker missed punching (item #982), built to
// Ben's approved demo of 2026-10-26: worker, building with its main cleaner in
// brackets, the day, in and out on the building's clock, reason — every field
// editable — and a button that names what it records. The building list holds
// only buildings the save accepts (manualShiftBuildings, the Timecards rule).
// The save is commitTimeAction() → manualSegment(), the Timecards save.
// ============================================================================

import { useMemo, useState } from 'react';
import { Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { CopilotProposalBase } from '../../../shared/types';
import { useAuth } from '../../../shared/auth';
import { canReviewTime, hasFullAmAuthority, isAmRole } from '../../../shared/role-gates';
import { useFacilityOptions, useServiceLines, useSiteTz, useWorkerPicker } from '../../time-labor/hooks/use-time-data';
import { manualShiftBuildings } from '../../time-labor/timecards';
import { WorkerPicker } from '../../time-labor/worker-picker';
import { tzLongLabel } from '../../../lib/siteTime';
import { addDays, crossesMidnight } from '../../../../supabase/functions/am-copilot/time';
import { fieldsFromDraft, hoursOf, timeMissing, type TimeDraft, type TimeFields } from '../time-actions';
import { CardShell } from './card-shell';

interface Props {
  proposal: CopilotProposalBase;
  isCommitting: boolean;
  onDoIt: (edited_input: Record<string, unknown>, was_edited: boolean) => void;
  onDiscard: () => void;
}

const inputClass =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-2 py-1.5 text-base text-tcs-text focus:border-tcs-text-muted focus:outline-none focus:ring-1 focus:ring-tcs-text-muted';

function dayWords(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}
function clock12(t: string): string {
  let h = Number(t.slice(0, 2));
  const ap = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${t.slice(3, 5)} ${ap}`;
}

export function TimeActionCard({ proposal, isCommitting, onDoIt, onDiscard }: Props) {
  const draft = proposal.input as unknown as TimeDraft;
  const ctx = (draft.context ?? {}) as Record<string, unknown>;
  const start = useMemo(() => fieldsFromDraft(draft), [draft]);
  const [f, setF] = useState<TimeFields>(start);
  const set = (patch: Partial<TimeFields>) => setF((p) => ({ ...p, ...patch }));

  const { profile } = useAuth();
  const role = profile?.role ?? 'none';
  const facilities = useFacilityOptions();
  const workers = useWorkerPicker();
  const serviceLines = useServiceLines();
  const site = useSiteTz(f.facility_id || null, null);
  const tz = site.tz ?? f.tz;

  // Only the buildings the save accepts for this person (the drafted one is
  // listed by name below while the list loads).
  const buildings = useMemo(() => manualShiftBuildings(
    { isStaff: canReviewTime(role), amSide: isAmRole(role), fullAm: hasFullAmAuthority(role), myEid: profile?.employee_id ?? null },
    facilities.rows,
  ), [role, profile?.employee_id, facilities.rows]);

  const fields: TimeFields = { ...f, tz };
  const missing = timeMissing(fields);
  const hours = missing ? null : hoursOf(fields);
  const workerName = workers.rows.find((w) => w.worker_id === f.worker_id)?.display_name ?? String(ctx.worker_name ?? f.worker_id);
  const buildingName = facilities.rows.find((b) => b.facility_id === f.facility_id)?.display ?? String(ctx.building ?? f.facility_id);
  const nextDay = crossesMidnight(f.in_time, f.out_time);
  const sameRecord = f.worker_id === draft.worker_id && f.day === draft.day;
  const already = Array.isArray(ctx.already) ? (ctx.already as string[]) : [];
  const wasEdited = JSON.stringify({ ...f, tz: null }) !== JSON.stringify({ ...start, tz: null });

  return (
    <CardShell
      title="Record a shift by hand"
      icon={<Clock className="h-4 w-4" />}
      accentClass="border-st-warn-border-strong"
      status={proposal.status}
      committedLabel={hours != null ? `Recorded · ${hours.toFixed(2)} h. Your name is on both punches.` : 'Recorded'}
      isCommitting={isCommitting}
      doItDisabled={!!missing}
      doItDisabledReason={missing ?? undefined}
      readBack={hours != null
        ? `${hours.toFixed(2)} hours for ${workerName} at ${buildingName}, ${dayWords(f.day)} ${clock12(f.in_time)} – ${nextDay ? `${dayWords(addDays(f.day, 1))} ` : ''}${clock12(f.out_time)}. Both punches are recorded as typed by hand, with your name on them.`
        : undefined}
      doItLabel={hours != null ? `Record ${hours.toFixed(2)} hours for ${workerName}` : 'Record shift'}
      onDoIt={() => onDoIt({ ...draft, fields }, wasEdited)}
      onDiscard={onDiscard}
      after={<Link to="/time/timecards" className="text-sm font-medium text-tcs-text underline">Open Timecards</Link>}
    >
      <label className="block">
        <span className="mb-1 block text-xs font-medium text-tcs-text-muted">Worker</span>
        <WorkerPicker rows={workers.rows} value={f.worker_id || null} onChange={(id) => set({ worker_id: id })} placeholder="Worker…" />
      </label>
      <label className="block">
        <span className="mb-1 block text-xs font-medium text-tcs-text-muted">Building</span>
        <select value={f.facility_id} onChange={(e) => set({ facility_id: e.target.value })} className={inputClass}>
          {!buildings.some((b) => b.facility_id === f.facility_id) && <option value={f.facility_id}>{buildingName}</option>}
          {buildings.map((b) => <option key={b.facility_id} value={b.facility_id}>{b.display}</option>)}
        </select>
      </label>
      <label className="block">
        <span className="mb-1 block text-xs font-medium text-tcs-text-muted">Service line</span>
        <select value={f.service_line_code} onChange={(e) => set({ service_line_code: e.target.value })} className={inputClass}>
          {serviceLines.map((l) => <option key={l.service_line_code} value={l.service_line_code}>{l.label}</option>)}
        </select>
      </label>
      <label className="block">
        <span className="mb-1 block text-xs font-medium text-tcs-text-muted">Day the shift started</span>
        <input type="date" value={f.day} onChange={(e) => set({ day: e.target.value })} className={inputClass} />
      </label>
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-tcs-text-muted">In (building&rsquo;s clock)</span>
          <input type="time" value={f.in_time} onChange={(e) => set({ in_time: e.target.value })} className={inputClass} />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-tcs-text-muted">Out (building&rsquo;s clock)</span>
          <input type="time" value={f.out_time} onChange={(e) => set({ out_time: e.target.value })} className={inputClass} />
        </label>
      </div>
      <p className="rounded-md bg-st-info-bg px-3 py-2 text-sm text-tcs-text">
        {tz ? `${tzLongLabel(tz)}. ` : 'Reading the building’s clock… '}
        {!missing && <>In <b>{dayWords(f.day)}, {clock12(f.in_time)}</b> → out <b>{nextDay ? dayWords(addDays(f.day, 1)) : dayWords(f.day)}, {clock12(f.out_time)}</b>{nextDay ? ' — the next morning' : ''} · <b>{hours?.toFixed(2)} h</b></>}
      </p>
      <label className="block">
        <span className="mb-1 block text-xs font-medium text-tcs-text-muted">Reason <span className="font-normal">optional</span></span>
        <input value={f.reason} onChange={(e) => set({ reason: e.target.value })} className={inputClass} />
      </label>
      {sameRecord && (
        <div className="flex min-h-[44px] items-center justify-between gap-3 rounded-md border border-tcs-border px-2 py-1.5 text-sm">
          <span className="text-xs text-tcs-text-muted">Already on {String(ctx.worker_name ?? 'the')}&rsquo;s record that day</span>
          <span className="text-right text-tcs-text">{already.length ? already.join(' · ') : 'nothing'}</span>
        </div>
      )}
    </CardShell>
  );
}
